import React, {useState, useEffect} from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useAxiosPrivate from '../../context/hooks/useAxiosPrivate';
import { Spinner, Button, Table, Form } from 'react-bootstrap';
import '../../appstyles/ClientsCSS/ClientsDetails.css'
import '../../appstyles/QuotesCSS/OrderDetails.css'

const unitNames = {
    KG: 'Kilogramos',
    G: 'Gramos',
    TON: 'Tonelada',
    TON_S: 'Tonelada corta',
    TON_L: 'Tonelada larga',
    LB: 'Libra',
    OZ: 'Onzas',
    L: 'Litro',
    ML: 'Mililitro',
    GAL_UK: 'Galón UK',
    GAL_US: 'Galón US',
    FCL: 'FCL',
};

export const observationsSchema = Yup.object().shape({
  observations: Yup.string()
    .required('Este campo es requerido')
    .min(10, 'El campo debe tener al menos 10 caracteres')
});

function InventoryDetails() {
  const axiosInstance = useAxiosPrivate();
  const { inventoryId } = useParams();
  const [inventory, setInventory] = useState(null);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm(
    {
      resolver: yupResolver(observationsSchema),
    }
  )

  const fetchInventoryDetails = () => {
    axiosInstance.get(`api/logistics-inventory/${inventoryId}/`)
      .then(response => {
        const InventoryDetails = response.data;
        setInventory(InventoryDetails);
        reset({ observations: InventoryDetails.observations })
      })
      .catch(error => {
        console.error('Failed to fetch inventory details', error);
        alert('Error al cargar los detalles del inventario. Por favor, inténtelo de nuevo más tarde.');
      });
  };
  
  useEffect(() => {
    fetchInventoryDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, axiosInstance]);

  function capitalizeFirst(text) {
    if (!text) return '';
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  const handleSaveObservations = (data) => {
    setLoading(true)
    axiosInstance.patch(`api/logistics-inventory/${inventoryId}/`, {
      observations: data.observations
      })
      .then(() => {
        fetchInventoryDetails();
        setEditing(false);
        setLoading(false)
      })
      .catch(error => {
        console.error('Failed to save supplier consecutive', error);
      });
  };

  const handleCancel = () => {
    setEditing(false);
  };

  if (!inventory) {
    return(
    <div className='loading-container'>
      <p style={{fontFamily: 'Roboto, sans-serif'}}>Cargando...</p>
      <Spinner animation="border" variant="info" style={{width: '4.5rem', height: '4.5rem'}} />
    </div>
    );
  }

  return (
    <div className='maestros-details'>
      <h2>Detalles de Inventario</h2>
      <hr />

      <h4 style={{fontWeight: '700'}}>Inventario No. {inventory.inventory_id}</h4>
      <div style={{display: 'flex', flexDirection: 'row'}}>
      <div className='first-col'>
          <p><strong>Producto No {inventory.product_id}:</strong> {inventory.product.product_name} </p>
          <p><strong>Fabricante:</strong> {capitalizeFirst(inventory.product.manufacturer)} </p>
          <p><strong>ID de la Cotización:</strong> {inventory.parent_quote.quote_id} </p>
          <p><strong>Consecutivo:</strong> {inventory.parent_quote.consecutive} </p>
          <p><strong>Cliente:</strong> {capitalizeFirst(inventory.parent_quote.client_name)} </p>
          <p><strong>Proveedor:</strong> {capitalizeFirst(inventory.parent_quote.supplier_name)} </p>
      </div>  

      <div className='second-col'>
          <p><strong>Incoterm:</strong> {inventory.parent_quote.incoterm} </p>
          <p><strong>Stock Inicial:</strong> {inventory.opening_stock} {unitNames[inventory.unit]} </p>
          <p><strong>Stock:</strong> {inventory.stock} {unitNames[inventory.unit]} </p>
          <p><strong>Estado: </strong> 
          <span style={{ color: inventory.status === 'Disponible' ? 'green' : inventory.status === 'No Disponible' ? 'red' : 'black' }}>
            {inventory.status}
          </span>
          </p>
          <p><strong>Estado de Tracking: </strong>
            <span style={{ color: inventory.tracking_status === 'Cerrado' ? 'green' : 'black' }}>
            {inventory.tracking_status}
          </span>
          </p>
      </div>   
      </div>
      <hr/>

      <div className='orders-details-actions' 
        style={{
          marginBottom: '10px'
        }}
      >
      <h4 style={{fontWeight: '700'}}>Observaciones</h4>
      <div className='orders-actions'>
      {!editing && ( 
        <button 
        className='btn btn-primary' 
        style={{backgroundColor: '#025C5A', borderColor: '#025C5A'}}
        onClick={() => setEditing(true)}
        > 
          Editar
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16" style={{marginLeft: '10px'}}> 
            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
            <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
          </svg>
        </button>
      )}
      </div>
      </div>  
      {editing ? (
      <Form onSubmit={handleSubmit(handleSaveObservations)}>
      <Form.Group controlId="observations">
        <Form.Control 
          as="textarea" 
          rows={5}  
          placeholder="Observaciones"
          {...register('observations')}
          isInvalid={!!errors.observations}
        />
        <Form.Control.Feedback type="invalid">
          {errors.observations?.message}
        </Form.Control.Feedback>
        </Form.Group> 

        <Button
          variant="secondary"
          type="Submit"
          style={{
            width: 'auto',
            backgroundColor: '#056664',
            border: ' solid 2px #025C5A',
            margin: '0 2px'
          }}
        >
          {loading ? 'Cargando...' : 'Guardar'}
        </Button>
        <Button
          variant="secondary"
          onClick={handleCancel}
          style={{
            width: 'auto',
            backgroundColor: '#056664',
            border: ' solid 2px #025C5A',
            margin: '0 2px'
          }}
        >
          Cancelar
        </Button>
      </Form>    
      ) : (
        <p> {inventory.observations ? inventory.observations : 'No hay observaciones para este inventario' } </p>
      )}
      <hr/>

      <h4 style={{fontWeight: '700', color: '#07514F'}}>{inventory.assigned_quotes.length > 1 ? 'Cotizaciones Asociadas' : 'Cotización Asociada'}</h4>
      {inventory.assigned_quotes.length === 0 ? (
        <div className='no-quotes-message'
        style={{ 
            textAlign: 'center', 
            padding: '15px', 
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '5px',
            color: '#721c24',
            fontSize: '16px',
            margin: '10px auto',
            width: '80%',
            marginLeft: 'auto',
            marginRight: 'auto',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            transition: 'background-color 0.3s ease, border-color 0.3s ease'
        }}
        >
        <p>No hay cotizaciones asociadas a este inventario.</p>
        <p>Por favor, asegúrese de que el inventario esté correctamente asignado a una cotización.</p>
        </div>
      ) : (
      <div>
      <Table striped bordered hover className='quotes-table'>
        <thead className='table-header'>
          <tr>
            <th>ID</th>
            <th>Consecutivo</th>
            <th>Cliente</th>
            <th style={{ maxWidth: '150px' }}>Consecutivo del cliente</th>
            <th>Proveedor</th>
            <th>Incoterm</th>
            <th>Cantidad</th>
          </tr>
        </thead>
        <tbody>
        {inventory.assigned_quotes.map(quotes => (
          <tr key={quotes.quote_id}>
            <td>{quotes.quote_id}</td>
            <td>{quotes.consecutive}</td>
            <td>{quotes.client_name}</td>
            <td style={{ maxWidth: '150px' }}>{quotes.client_consecutive}</td>
            <td>{quotes.supplier_name}</td>
            <td>{quotes.incoterm}</td>
            <td>{quotes.product_quantity} {unitNames[quotes.product_unit]}</td>
          </tr>
        ))}
        </tbody>
        </Table>
      </div>
      )}

      <Button 
        variant="secondary" 
        style={{marginTop: '20px', width: '20%'}}
        onClick={() => navigate(`/inventorylist`)}
        >
          Volver
      </Button>
  
    </div>
  )
}

export default InventoryDetails